import React, { useState, useMemo } from "react";
import { apiService } from "./services/apiService";
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaDownload, 
  FaEnvelope, 
  FaFileExcel, 
  FaSort, 
  FaSortUp, 
  FaSortDown 
} from "react-icons/fa";
import * as XLSX from "xlsx";
import "./styles/Reports.css";

function RenewAdmissionList({ onBack }) {
  const [days, setDays] = useState(30);
  const [studentsList, setStudentsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  // State to manage sorting configuration
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    apiService.getRenewAdmissionList(days)
      .then((data) => { 
        setStudentsList(data || []); 
        setLoading(false); 
      })
      .catch((err) => { 
        console.error(err); 
        setLoading(false); 
      });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return "N/A";
    return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  // Sorting Handler Logic
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Memoized sorted array processing computation
  const sortedStudentsList = useMemo(() => {
    let sortableItems = [...studentsList];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // Keep null/undefined records consistently at the bottom
        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;

        // Specialized comparison execution for dates
        if (sortConfig.key === "startDate" || sortConfig.key === "endDate") {
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        // Default alphabetic string and regular localized value checking 
        const stringA = String(aValue).toLowerCase();
        const stringB = String(bValue).toLowerCase();

        if (stringA < stringB) return sortConfig.direction === "asc" ? -1 : 1;
        if (stringA > stringB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [studentsList, sortConfig]);

  // Renders state-dependent sort directional styling arrows inline inside headers
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort style={{ marginLeft: "6px", opacity: 0.4 }} />;
    return sortConfig.direction === "asc" 
      ? <FaSortUp style={{ marginLeft: "6px", color: "#007bff" }} /> 
      : <FaSortDown style={{ marginLeft: "6px", color: "#007bff" }} />;
  };

  // Upgraded Instance Generator parsing the contextually sorted data arrays with Sr No.
  const buildPDFDocInstance = async () => {
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF();
    
    doc.text("Renewal Due Checklist", 14, 15);
    doc.setFontSize(9);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 21);

    autoTable(doc, {
      startY: 26,
      head: [["Sr No", "Reg", "Name", "Mobile", "Seat", "Start", "End", "Days Left"]],
      body: sortedStudentsList.map((s, index) => [
        index + 1,
        s.registrationNumber, 
        s.studentName, 
        s.mobileNumber, 
        s.seatCode, 
        s.startDate, 
        s.endDate, 
        getDaysRemaining(s.endDate)
      ])
    });
    return doc;
  };

  const handleDownloadXls = () => {
    const wb = XLSX.utils.book_new();
    
    // Explicitly format dataset rows with Sr No to keep columns clean and professional
    const formattedData = sortedStudentsList.map((s, index) => ({
      "Sr No": index + 1,
      "Reg No.": s.registrationNumber,
      "Name": s.studentName,
      "Mobile": s.mobileNumber,
      "Seat": s.seatCode,
      "Start Date": s.startDate,
      "End Date": s.endDate,
      "Days Left": getDaysRemaining(s.endDate)
    }));

    // Append Generated Timestamp row at the bottom
    formattedData.push({
      "Sr No": "",
      "Reg No.": "Report Generated On:",
      "Name": new Date().toLocaleString()
    });

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(formattedData), "Renewals");
    XLSX.writeFile(wb, "Renewal-List.xlsx");
  };

  const handleEmailBoth = async () => {
    setEmailing(true);
    try {
      const doc = await buildPDFDocInstance();
      const pdfBlob = new Blob([doc.output("arraybuffer")], { type: "application/pdf" });
      const wb = XLSX.utils.book_new();
      
      const formattedData = sortedStudentsList.map((s, index) => ({
        "Sr No": index + 1,
        "Reg No.": s.registrationNumber,
        "Name": s.studentName,
        "Mobile": s.mobileNumber,
        "Seat": s.seatCode,
        "Start Date": s.startDate,
        "End Date": s.endDate,
        "Days Left": getDaysRemaining(s.endDate)
      }));

      // Append Generated Timestamp row at the bottom of emailing excel sheet
      formattedData.push({
        "Sr No": "",
        "Reg No.": "Report Generated On:",
        "Name": new Date().toLocaleString()
      });

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(formattedData), "Renewals");
      const xlsBlob = new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

      const fd = new FormData();
      fd.append("pdf", pdfBlob, "Renewal.pdf");
      fd.append("xlsx", xlsBlob, "Renewal.xlsx");
      await apiService.emailRenewList(fd);
      alert("Email sent successfully!");
    } catch (err) { 
      alert("Email failed."); 
    }
    setEmailing(false);
  };

  return (
    <div className="rp-page">
      <div className="rp-header-card">
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div className="rp-header-icon"><FaCalendarAlt size={20} /></div>
          <h2 className="rp-title" style={{ margin: 0 }}>Renewal Checklist</h2>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {studentsList.length > 0 && (
            <>
              <button onClick={() => buildPDFDocInstance().then(d => d.save("List.pdf"))} className="rp-btn-back">
                <FaDownload /> PDF
              </button>
              <button onClick={handleDownloadXls} className="rp-btn-back">
                <FaFileExcel /> Excel
              </button>
              <button onClick={handleEmailBoth} disabled={emailing} className="rp-btn-back">
                <FaEnvelope /> {emailing ? "Sending..." : "Email Both"}
              </button>
            </>
          )}
          <button onClick={onBack} className="rp-btn-back"><FaArrowLeft /> Back To Dashboard</button>
        </div>
      </div>
      
      <div className="rp-header-card">
        <form onSubmit={handleSearch} style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <label style={{ fontSize: "13px", fontWeight: "700", color: "#7a8ba6", userSelect: "none" }}>
            NUMBER OF DAYS:
          </label>
          <input 
            type="number" 
            value={days} 
            onChange={e => setDays(e.target.value)} 
            className="rp-input" 
          />
          <button type="submit" className="rp-btn-back">Search</button>
        </form>
      </div>

      {studentsList.length > 0 && (
        <div className="rp-card">
          <div className="rp-table-wrapper">
            <table className="rp-table">
              <thead>
                <tr>
                  <th style={{ userSelect: "none", width: "50px" }}>Sr No</th>
                  <th onClick={() => handleSort("registrationNumber")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Reg No. {renderSortIcon("registrationNumber")}
                  </th>
                  <th onClick={() => handleSort("studentName")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Name {renderSortIcon("studentName")}
                  </th>
                  <th onClick={() => handleSort("mobileNumber")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Mobile {renderSortIcon("mobileNumber")}
                  </th>
                  <th onClick={() => handleSort("seatCode")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Seat {renderSortIcon("seatCode")}
                  </th>
                  <th onClick={() => handleSort("startDate")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Start {renderSortIcon("startDate")}
                  </th>
                  <th onClick={() => handleSort("endDate")} style={{ cursor: "pointer", userSelect: "none" }}>
                    End {renderSortIcon("endDate")}
                  </th>
                  <th onClick={() => handleSort("endDate")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Days {renderSortIcon("endDate")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedStudentsList.map((s, index) => (
                  <tr key={s.id || index}>
                    <td style={{ fontWeight: "600", color: "#555" }}>{index + 1}</td>
                    <td>{s.registrationNumber}</td>
                    <td className="rp-td-name">{s.studentName}</td>
                    <td>{s.mobileNumber}</td>
                    <td><span className="rp-seat-badge">{s.seatCode}</span></td>
                    <td>{new Date(s.startDate).toLocaleDateString()}</td>
                    <td>{new Date(s.endDate).toLocaleDateString()}</td>
                    <td>{getDaysRemaining(s.endDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default RenewAdmissionList;